import { format } from 'date-fns';
import * as moment from 'moment';

const ROME_TZ = 'Europe/Rome';

/**
 * Parses a backend UTC wall-clock timestamp (LocalDateTime without offset) as UTC,
 * returning a Date suitable for local display.
 */
export function parseUtcTimestamp(value: string | Date | null | undefined): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    // Already a UTC instant (e.g. from moment.utc(string).toDate()). Do not re-read local
    // getters as UTC wall-clock — that adds +2h for CEST browsers.
    return value;
  }
  const m = moment.utc(value);
  if (!m.isValid()) {
    return null;
  }
  return m.toDate();
}

/** Maps reservation createdAt to a Date for display; keeps paidAt/refundedAt as strings for filters. */
export function withReservationCreatedAtForDisplay<T extends { createdAt?: string | Date | null }>(
  reservation: T
): T {
  const createdAt = parseUtcTimestamp(reservation.createdAt);
  if (!createdAt) {
    return reservation;
  }
  return { ...reservation, createdAt };
}

/**
 * Formats a UTC wall-clock timestamp in the browser's local timezone.
 */
export function formatUtcTimestampLocal(
  value: string | Date | null | undefined,
  pattern = 'dd/MM/yyyy HH:mm'
): string {
  if (value == null) {
    return '';
  }
  try {
    if (value instanceof Date) {
      return format(value, pattern);
    }
    const m = moment.utc(value);
    if (!m.isValid()) {
      return '';
    }
    return format(m.toDate(), pattern);
  } catch {
    return '';
  }
}

/** Table cell: local calendar date + optional time (contacts / orders list pattern). */
export function formatUtcTimestampCell(
  value: string | Date | null | undefined,
  options?: { omitMidnightTime?: boolean }
): { date: string; time?: string } | null {
  if (value == null) {
    return null;
  }
  const date = formatUtcTimestampLocal(value, 'dd/MM/yyyy');
  if (!date) {
    return { date: '—' };
  }
  const time = formatUtcTimestampLocal(value, 'HH:mm');
  const omitMidnight = options?.omitMidnightTime !== false;
  const showTime = !!time && (!omitMidnight || time !== '00:00');
  return { date, time: showTime ? time : undefined };
}

/**
 * Experience slot (startDateTime / date+time): naive wall-clock, not a UTC instant.
 * Do not use {@link formatUtcTimestampCell} — that shifts the slot by the browser offset.
 */
export function formatExperienceSlotCell(
  startDateTime?: string | Date | null,
  date?: string | Date | null,
  time?: string | Date | null,
  options?: { omitMidnightTime?: boolean }
): { date: string; time?: string } | null {
  let raw: string | null = null;
  if (startDateTime != null && String(startDateTime).trim()) {
    raw = String(startDateTime).trim();
  } else if (date != null && time != null) {
    const day = String(date).trim().slice(0, 10);
    const tod = String(time).trim();
    if (day && tod) {
      raw = `${day}T${tod}`;
    }
  }
  if (!raw) {
    return null;
  }
  // Parse as local wall-clock (no moment.utc) so HH:mm matches the booked slot.
  const m = moment(raw);
  if (!m.isValid()) {
    return { date: '—' };
  }
  const dateStr = m.format('DD/MM/YYYY');
  const timeStr = m.format('HH:mm');
  const omitMidnight = options?.omitMidnightTime !== false;
  const showTime = !!timeStr && (!omitMidnight || timeStr !== '00:00');
  return { date: dateStr, time: showTime ? timeStr : undefined };
}

/** Single line for detail headers: `dd/MM/yyyy` or `dd/MM/yyyy, HH:mm` in local timezone. */
export function formatUtcTimestampDateTimeLine(
  value: string | Date | null | undefined,
  options?: { alwaysShowTime?: boolean }
): string {
  const cell = formatUtcTimestampCell(value, {
    omitMidnightTime: !options?.alwaysShowTime,
  });
  if (!cell) {
    return '';
  }
  if (!cell.time) {
    return cell.date;
  }
  return `${cell.date}, ${cell.time}`;
}

/**
 * Business order date ({@code orders.date} / LocalDate): date only — midnight time is not shown.
 */
export function formatOrderBusinessDateCell(
  value: string | Date | null | undefined
): { date: string; time?: string } | null {
  if (value == null) {
    return null;
  }
  const s = String(value).trim();
  if (!s) {
    return null;
  }
  const day = s.length >= 10 ? s.slice(0, 10) : s;
  const m = moment(day, 'YYYY-MM-DD', true);
  if (m.isValid()) {
    return { date: m.format('DD/MM/YYYY') };
  }
  const fallback = moment(s);
  if (!fallback.isValid()) {
    return { date: '—' };
  }
  return { date: fallback.format('DD/MM/YYYY') };
}

export function formatOrderBusinessDateLine(value: string | Date | null | undefined): string {
  const cell = formatOrderBusinessDateCell(value);
  return cell?.date ?? '';
}

const LOCAL_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function orderDateDayPart(value: string | Date): string {
  const s = String(value).trim();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function isOrderBusinessDateOnly(value: string | Date | null | undefined): boolean {
  if (value == null) {
    return false;
  }
  const s = String(value).trim();
  if (!s) {
    return false;
  }
  return LOCAL_DATE_ONLY.test(orderDateDayPart(s)) && s.length <= 10;
}

/**
 * Order date cell: LocalDate (YYYY-MM-DD) → date only; full timestamp → UTC wall-clock in local TZ.
 */
export function formatOrderDateCell(
  value: string | Date | null | undefined,
  options?: { omitMidnightTime?: boolean }
): { date: string; time?: string } | null {
  if (value == null) {
    return null;
  }
  const s = String(value).trim();
  if (!s) {
    return null;
  }
  if (isOrderBusinessDateOnly(s)) {
    return formatOrderBusinessDateCell(orderDateDayPart(s));
  }
  return formatUtcTimestampCell(s, { omitMidnightTime: options?.omitMidnightTime ?? false });
}

/** Single-line order date for export (CSV/XLSX). */
export function formatOrderDateForExport(value: string | Date | null | undefined): string {
  if (value == null) {
    return '';
  }
  const s = String(value).trim();
  if (!s) {
    return '';
  }
  if (isOrderBusinessDateOnly(s)) {
    return formatOrderBusinessDateLine(orderDateDayPart(s));
  }
  return formatUtcTimestampLocal(s, 'dd/MM/yyyy HH:mm');
}

/** Formats a UTC timestamp in Europe/Rome (24h). Default: dd/MM/yyyy HH:mm */
export function formatUtcTimestampRome(
  value: string | Date | null | undefined,
  pattern = 'dd/MM/yyyy HH:mm'
): string {
  const parsed = parseUtcTimestamp(value);
  if (!parsed) {
    return '';
  }
  if (pattern !== 'dd/MM/yyyy HH:mm') {
    return formatUtcTimestampLocal(value, pattern);
  }
  const dateStr = new Intl.DateTimeFormat('en-GB', {
    timeZone: ROME_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
  const timeStr = new Intl.DateTimeFormat('it-IT', {
    timeZone: ROME_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
  return `${dateStr} ${timeStr}`;
}

export function serializeUtcWallClockTimestamp(
  value: string | Date | null | undefined
): string {
  if (value == null || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return moment(value).utc().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
  }
  const m = moment.parseZone(String(value));
  if (!m.isValid()) {
    return String(value);
  }
  return m.format('YYYY-MM-DDTHH:mm:ss') + 'Z';
}
